import { defaultStyleOptions, StyleOptions } from 'botframework-webchat-api';
import createDefaultAdaptiveCardHostConfig from 'botframework-webchat/lib/adaptiveCards/Styles/adaptiveCardHostConfig'
import Composer, { ComposerProps } from 'botframework-webchat-component/lib/Composer';
import { DruidAction } from './hooks/useWebChatReducer';
import { randomHash } from './utils/randomHash';
import { druidCreateBlobUrl } from './utils/urlUtils';
import { BotLanguage, WebChatHeaderOptions, WebChatState } from './WebChatContext';
import { typingAnimation } from '../resources/images/druidAnimations'
import * as merge from 'deepmerge';
import createStyleSet from 'botframework-webchat/lib/createFullStyleSet';

export type BotFrameworkOptions = Omit<ComposerProps, "directLine" | "userID">;
export type BotFrameworkMiddlewares = Pick<ComposerProps, "activityMiddleware" | "activityStatusMiddleware" | "attachmentForScreenReaderMiddleware"| "attachmentMiddleware" 
                                                        | "avatarMiddleware" | "cardActionMiddleware"| "groupActivitiesMiddleware" | "scrollToEndButtonMiddleware" | "toastMiddleware"  
                                                        | "typingIndicatorMiddleware">;

export type BotFrameworkStyleOptions =  Pick<BotFrameworkOptions, "styleOptions">;

export type DruidUIOptions = {
    autoExpandEnabled?: boolean,
    containerElement?: Nullable<HTMLElement>,
    closeButtonEnabled?: Nullable<boolean>,
    height?: Nullable<string | number>,
    width?: Nullable<string | number>,
    minWidth?: Nullable<string | number>,
    chatTitle?: string,
    parentElement?: Nullable<HTMLElement>,
    contextMenuIcon?: string,
    position?: 'left' | 'right', // same type as state.side
    showUploadButton?: Nullable<boolean>, // v1 specific. same functionality as styleOptions.hideUploadButton
    floatRight?: Nullable<number>,
} & Partial<{ // v2 UI options
    bottom?: Nullable<string | number>,
    top?: Nullable<string | number>,

    isVoiceInput: Nullable<boolean>,
    v2_chatTitle_icon: string,
    v2_minimize_icon: string | Function,
    v2_switch_icon: string,
    hideDruidSendBox?: Nullable<boolean>,
    hideSuggestedActions?: Nullable<boolean>,
    disableDruidSendBox?: Nullable<boolean>, // to remove
    headerOptions?: Nullable<Partial<WebChatHeaderOptions>>,

    druidSendBoxPaddingLeftRight: Nullable<number>,
    druidSendBoxPaddingTop: Nullable<number>, //outer padding of sendBox
    druidSendBoxPaddingBottom: Nullable<number>,
    druidSendBoxTextBoxPadding: Nullable<number> //inner padding of sendBox (textbox padding actually). adds height to sendBox
    druidSendBoxInnerPaddingLeftRight: Nullable<number> //inner leftright padding of sendBox
    druidSendBoxInnerPaddingTopBottom: Nullable<number>, //inner topbottom padding of sendBox
    druidSendBoxInnerBorderRadius: Nullable<number>,
    

    buttonsPadding: Nullable<number>,
    buttonsTextColor: Nullable<string>,
    buttonsBorderRadius: Nullable<number>,
    buttonsBorderColor: Nullable<string>,
    buttonsBackgroundColor: Nullable<string>,
    buttonsBackgroundColorOnHover: Nullable<string>,
    buttonsTextColorOnHover: Nullable<string>,
    buttonsBorderColorOnHover: Nullable<string>,

    activityStatusReadColor: Nullable<string>,
    activityStatusBotName: Nullable<string>,
    activityStatusUserName: Nullable<string>,
    activityStatusShowBotName: Nullable<boolean>,
    activityStatusShowUserName: Nullable<boolean>,

    chatBoxBorderRadius: Nullable<number>,
    chatBoxBottomRadius: Nullable<number>,
    chatBoxBorderColor: Nullable<string>,
    chatBoxBorderSize: Nullable<number>,

    headerTextColor: Nullable<string>,
    headerTextColorOnHover: Nullable<string>,
    headerFontSize: Nullable<number | string>,
    headerBottomBorderRadius: Nullable<number | string>,
    headerOuterPadding: Nullable<number | string>,
    headerBackground: Nullable<string>,
    
    transcriptFontSize: Nullable<string | number>,
    sendBoxFontSize: Nullable<string | number>,
    lineHeight: Nullable<string | number>,
    rootFontSize: Nullable<string | number>,
}>;
export type WebChatOptions = BotFrameworkOptions //& Partial<FullComposerProps>
    & {
        instanceID: string,
    }
    & Partial<{
        botId: Nullable<string>,
        baseUrl: Nullable<string>,
        queryParams: Nullable<string>,
        authorizationUrl: Nullable<string>,
        authorizationBaseUrl: Nullable<string>,
        userId: string // should we remove ?
        username: string // should we remove ? this is not typo error. "username" in bf4

        /* cookies */
        druidCookieConsentEnabled: Nullable<boolean>,
        conversationTokenEnabled: Nullable<boolean>,
        conversationToken_namePrefix: Nullable<string>,
        druidWebChatIsOpenedEnabled: Nullable<boolean>,
        druidWebChatIsOpened_namePrefix: Nullable<string>, 
        cookieDomain: Nullable<string>,  // druidConversationTokenDomain

        isDisabled: boolean,
        withCredentials: Nullable<boolean>,
        withCredentials_authorization: Nullable<boolean>, //override for 'withCredentials' -> specific for authorizationCall
        isWidget?: boolean,
        backgroundTrafficEnabled?: boolean,
        heartBeatUrl: Nullable<string>,

        UI?: DruidUIOptions,
    }> 
    & Partial<{ 
        middlewares?: BotFrameworkMiddlewares, // you can provide string remote, or function localy
        //  activityMiddleware?: any // should we remove ? -> should not because it is oneormany<T> on ComposerProps
    }>
    & Partial<{ // specific for fullscreen webchat
        conversationHistoryActivities: any[], // not used
        authorizationResultDto?: any
    }>
    & Partial<{ // v2 specific
        languages: BotLanguage[],
        adaptiveCardsHostConfig: any,
        wideOptions: {
            UI?: DruidUIOptions
        } & BotFrameworkStyleOptions,
        designPatterns: {
            nonCarousel_HeroCard_AutoWidth: boolean,
            nonCarousel_HeroCard_ButtonsOnly_BackgroundTransparent: boolean
        },
        customDruidStyleSet?: Partial<ReturnType<typeof createStyleSet>>
        webChatEventHandler?: ((action: DruidAction, currentDruidState: WebChatState, currentBFState?: any) => void),
        _loadConfigurationJSON: any
    }>;

// @ts-ignore
const druidDefaultStyleOptions: StyleOptions = {
    ...defaultStyleOptions, // just to have the default options whatever the context
    ...{  // these are the default druid options override for bf4 webchat
        // groupTimestamp: 0, //to remove

        accent: '#70b244',
        paddingRegular: 20,
        primaryFont: 'Druid Regular',
        backgroundColor: 'rgba(255, 255, 255, 0.8)',

        botAvatarBackgroundColor: 'Transparent',

        hideSendBox: true,
        sendBoxBackground: '#f4f5f7',
        sendBoxBorderTop: 0,
        sendBoxTextWrap: true,
        sendBoxMaxHeight: 100, // used for text are maxHeight
        
        typingAnimationHeight: 40,
        typingAnimationBackgroundImage: druidCreateBlobUrl(typingAnimation, 'image/gif'),

        //  autoScrollSnapOnActivity: 1, // must be set to 1 or else error when a choice is first message
        autoScrollSnapOnPage: 90,
        autoScrollSnapOnActivityOffset: 100,
        scrollToEndButtonBehavior: 'any',

        // bubbleMaxWidth: '100%', // removed. because when width is large, bubbles are also large.
        bubbleFromUserBackground: '#d3d8e3',
        bubbleBackground: '#f4f5f7',
        bubbleBorderColor: '#e4e4e4',
        messageActivityWordBreak: 'break-word',
        bubbleNubSize: 0,
        bubbleFromUserNubSize: 0,
        bubbleBorderRadius: 20,
        bubbleFromUserBorderRadius: 20,
        bubbleImageHeight: 240, // was 'auto' but images are to large

        transcriptTerminatorBackgroundColor: 'Transparent', // to hide transcript terminator
        transcriptTerminatorColor: 'Transparent',
        

        suggestedActionBorderRadius: 10, // if not provided, it is inherited from UI.buttonsBorderRadius
        suggestedActionTextColor: '#70b244', // must be equal to UI.buttonsTextColor
        suggestedActionBackground: 'white',// must be equal to UI.buttonsBackgroundColor
        // suggestedActionBackgroundColor: 'white', // default from bf4
        suggestedActionLayout: 'flow', // override of carousel
    }
}


const defaultAdaptiveCardHostConfig = createDefaultAdaptiveCardHostConfig(druidDefaultStyleOptions);
export const webChatDefaultOptions: WebChatOptions =  {
    instanceID: randomHash(),
    userId: 'anonymous',
    backgroundTrafficEnabled: true,
    authorizationBaseUrl: null, // leave null for baseUrl, set '/' for containing website root url, set 'https://site.com' for full root url
    withCredentials: false,
    withCredentials_authorization: false,
    druidCookieConsentEnabled: false,
    conversationTokenEnabled: true,
    druidWebChatIsOpenedEnabled: true,
    conversationToken_namePrefix: "",
    druidWebChatIsOpened_namePrefix: "",    
    isDisabled: false,
    UI: {
        isVoiceInput: false,
        containerElement: null,
        closeButtonEnabled: false, // controls if containerElement should be hidden on expand
        autoExpandEnabled: false,
        width: 400,
        height: '60vh',
        minWidth: 400,
        bottom: 20,
        top: 'auto',
        chatTitle: 'Webchat',
        // contextMenuIcon: 'BsMenuUp',
        contextMenuIcon: 'FaBars',
        position: 'right',
        showUploadButton: null,
        floatRight: 20,

        buttonsPadding: (druidDefaultStyleOptions?.paddingRegular!) / 2,
        buttonsBorderRadius: (druidDefaultStyleOptions?.paddingRegular!) / 2,
        buttonsBorderColor:  (druidDefaultStyleOptions?.accent),
        buttonsBackgroundColor: 'white',
        buttonsTextColor: (druidDefaultStyleOptions?.accent),

        activityStatusShowUserName: true,
        activityStatusShowBotName: true,

        v2_minimize_icon: 'FaTimes',
        v2_switch_icon: 'BsArrowLeftRight',
        chatBoxBorderRadius: 4,
        chatBoxBorderColor: '#eee',
        chatBoxBorderSize: 1,

        headerTextColor: 'white',
        headerFontSize: '1.3em', // 1.3 larger than fontsize
        headerBottomBorderRadius: 25,

        transcriptFontSize: '$rootFontSize',
        sendBoxFontSize: '$rootFontSize',
        lineHeight: 'normal' // normal == 1.2 (browser dependent)

        // activityStatusReadColor: '#70b244'
    },
    styleOptions: druidDefaultStyleOptions,
    adaptiveCardsHostConfig: merge.all([
        // there is a problem when initialOptions are provided.
            defaultAdaptiveCardHostConfig,
            {
                spacing: {
                    padding: druidDefaultStyleOptions.paddingRegular,
                    // large: 30 // this is used, for ex, at the horizontalSeparator of adaptiveCards. see [actions.spacing]
                },
                actions: {
                    // buttonSpacing: 20,
                    // actionAlignment: 'left',
                    // actionsOrientation: 'vertical',
                    allowTitleToWrap: true,
                    
                    spacing: "medium"
                },
                fontFamily: 'Druid Regular'
            }
    ]),
    languages: [],
    conversationHistoryActivities: [],
    designPatterns: {
        nonCarousel_HeroCard_AutoWidth: false,
        nonCarousel_HeroCard_ButtonsOnly_BackgroundTransparent: true

    },
    // ...Components.Composer.defaultProps,
    ...Composer.defaultProps,
    suggestedActionsAccessKey: false // could be huge bug for accessibility. review AccessKeySinkSurface and AccessKeySinkContext
}


export const webChatFullScreenDefaultOptions: WebChatOptions = merge.all<WebChatOptions>([
         webChatDefaultOptions,
         {
            conversationTokenEnabled: false, // overriden by remote config. added only to take notice here.
            UI: {
                headerOptions: {
                    showCloseButton: false,
                    showChangeSideButton: false,
                    showLanguageSelector: true,
                    showAvatar: true
                }
        }}
]);

export enum DruidCookies {
    DruidWebChatIsOpened = "druidWebChatIsOpened",
    DruidConversationToken = "druidConversationToken",
    DruidConversationLanguage= "druidConversationLanguage",
    DruidCookieConsent= "druidCookieConsent",
}